import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AboutView } from '@/features/about/AboutView';
import { DetailView } from '@/features/detail/DetailView';
import { ExpertsView } from '@/features/experts/ExpertsView';
import { ListView } from '@/features/list/ListView';
import { ReportView } from '@/features/reports/ReportView';
import { ReportsView } from '@/features/reports/ReportsView';
import { SettingsView } from '@/features/settings/SettingsView';
import { SetupView } from '@/features/setup/SetupView';
import { useT } from '@/i18n';
import { ADAPTERS, MODELS_TTL_MS, pickDefaultModel, PROVIDER_ORDER } from '@/llm';
import { DevBanner } from '@/components/DevBanner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { startQuotePolling, stopQuotePolling } from '@/market/quoteService';
import { refreshPriceFeed } from '@/reports/priceFeed';
import { warmSymbolIndex } from '@/market/symbolIndex';
import { settingsStore, useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';

export default function App() {
  const app = useAppState();
  const settings = useSettings();
  const { locale } = useT();
  const location = useLocation();

  useEffect(() => {
    void appStore.init().then(() => {
      startQuotePolling();
      warmSymbolIndex();
    });
    void refreshStaleModels();
    void refreshPriceFeed();
    return () => stopQuotePolling();
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  if (!app.ready) return null;

  const firstRun = !settings.setupDone && app.portfolios.length === 0 && location.pathname !== '/setup';

  return (
    <>
      <DevBanner />
      <ErrorBoundary resetKey={location.pathname}>
      <Routes>
        <Route path="/setup" element={<SetupView />} />
        <Route path="/about" element={<AboutView />} />
        <Route path="/settings" element={<SettingsView />} />
        <Route path="/a/:assetId" element={<DetailView />} />
        <Route path="/experts" element={<ExpertsView />} />
        <Route path="/experts/:threadId" element={<ExpertsView />} />
        <Route path="/reports" element={<ReportsView />} />
        <Route path="/reports/:reportId" element={<ReportView />} />
        <Route path="/brief" element={<Navigate to="/reports" replace />} />
        <Route path="/" element={firstRun ? <Navigate to="/setup" replace /> : <ListView />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>
      {app.notice && (
        <div className={`toast${app.noticeLevel === 'error' ? ' error' : ''}`} role={app.noticeLevel === 'error' ? 'alert' : 'status'}>
          <span>{app.notice}</span>
          {app.noticeLevel === 'error' && <button type="button" className="toastClose" onClick={() => appStore.dismissNotice()} aria-label="close">×</button>}
        </div>
      )}
    </>
  );
}

async function refreshStaleModels() {
  const s = settingsStore.get();
  for (const p of PROVIDER_ORDER) {
    const saved = s.llm.providers[p];
    if (!saved?.apiKey) continue;
    const fresh = saved.modelsSource === 'live' && saved.modelsFetchedAt && Date.now() - saved.modelsFetchedAt < MODELS_TTL_MS;
    if (fresh) continue;
    try {
      const models = await ADAPTERS[p].listModels(saved.apiKey);
      settingsStore.updateProvider(p, { models, modelsFetchedAt: Date.now(), modelsSource: 'live', model: pickDefaultModel(models, saved.model) });
    } catch {
      // keep the last list
    }
  }
}
